import React from "react";
import { Fstyr } from "../../components/FstyrComponents/Fstyr.js";
import { fetchModel } from "../../components/HelperComponents/FetchExtension";
import URL from "../../Constants/URL";
import { BusinessCardVm } from "../../Models/BusinessCardVm";
import { BusinessVm } from "../../Models/BusinessVm";
import { DateFormatter } from "../../components/HelperComponents/DateFormatter";
import { GetStatusName } from "../../components/HelperComponents/StatusHelper";
import { sortCardTableByDate } from "../../components/HelperComponents/Helpers";
import { ValidationType } from "../../Constants/ValidationTypes";
import { formatAddress } from "../../components/HelperComponents/AddressFormatter.js";
import { Link } from "react-router-dom";

enum searchType {
  cvr = "CVR",
  cpr = "CPR",
}

type Props = {};

type State = {
  searchType: searchType;
  cvrOrCprInput: string;
  cvrOrCprIsValid: boolean;
  searchButtonPressed: boolean;

  showSpinner: boolean;

  businessVm: BusinessVm;
  tableResults: (JSX.Element | string)[][];
};

export class SearchBusinessCVR extends React.Component<Props, State> {
  private toastRef: React.RefObject<any>;

  constructor(props: Props) {
    super(props);
    this.toastRef = React.createRef();

    this.state = {
      searchType: searchType.cvr,
      cvrOrCprInput: "",
      cvrOrCprIsValid: false,
      searchButtonPressed: false,

      showSpinner: false,

      businessVm: new BusinessVm(),
      tableResults: [],
    };
  }

  handleSearch() {
    this.setState({ searchButtonPressed: true });
    this.clearPage(false, false);
    if (this.state.cvrOrCprIsValid) {
      this.setState({ showSpinner: true });
      this.fetchBusiness();
      this.fetchBusinessCards();
    }
  }

  fetchBusiness() {
    fetchModel("GET", URL + "/api/BusinessCard/GetBusinessByCvrOrCpr", this.state.cvrOrCprInput, BusinessVm)
      .then((model) => {
        if (model[0].Cvr.length >= 8) {
          // ikke == 8 fordi Cvr feltet kan indeholde et cpr hvis virksomheden er oprettet privat
          this.setState({ businessVm: model[0] });
        }
      })
      .catch((error) => this.showErrorToast(error, "en virksomhed"))
      .finally(() => {
        this.setState({ showSpinner: false });
      });
  }

  fetchBusinessCards() {
    fetchModel("GET", URL + "/api/BusinessCard/GetBusinessCardsByCvr", this.state.cvrOrCprInput, BusinessCardVm)
      .then((model) => {
        this.handleBusinessSearchResults(model);
      })
      .catch((error) => this.showErrorToast(error, "et virksomhedskort"))
      .finally(() => {
        this.setState({ showSpinner: false });
      });
  }

  handleBusinessSearchResults(result: BusinessCardVm[]) {
    result = sortCardTableByDate(result);
    this.setState({
      tableResults: result.map((businessCard) => [
        <Link target="_blank" to={`/businesscard/searchbusinesscard/${businessCard.CardNumber}`}>
          {businessCard.CardNumber}
        </Link>,
        businessCard.BusinessName,
        DateFormatter(businessCard.ValidFrom),
        DateFormatter(businessCard.ValidTo),
        GetStatusName(businessCard.Status.Id),
      ]),
    });
  }

  clearPage(clearRadioButtons: boolean, clearInput: boolean) {
    this.setState({
      showSpinner: false,
      businessVm: new BusinessVm(),
      tableResults: [],
    });

    if (clearInput) {
      this.setState({
        searchButtonPressed: false,
        cvrOrCprIsValid: false,
        cvrOrCprInput: "",
      });
    }

    if (clearRadioButtons) {
      this.setState({ searchType: searchType.cvr });
    }
  }

  showErrorToast(error: string, msg: string) {
    this.setState({ showSpinner: false });
    if (error === "No result") {
      this.toastRef.current.show("Virksomhed", `Der findes ikke ${msg} med det søgte nummer`, "warning");
    } else if (error === "Failed response") {
      this.toastRef.current.show("Fejl", "Kan ikke forbinde til databasen", "error");
    } else {
      this.toastRef.current.show("Fejl", "Der skete en ukendt fejl", "error");
    }
  }

  render() {
    return (
      <>
        <Fstyr.Toast ref={this.toastRef} />
        <Fstyr.Segment title="Søg Virksomhedskort" isPageTitle={true}>
          <div className="fstyr-right-top-button-group">
            <Fstyr.Button
              label="Ryd side"
              onClick={() => {
                this.clearPage(true, true);
              }}
            />
          </div>
        </Fstyr.Segment>
        <Fstyr.Segment title={`Angiv ${this.state.searchType}`}>
          <Fstyr.RadioButtonGroup
            label="Søg på"
            choices={["CVR", "CPR"]}
            value={this.state.searchType}
            onClick={(value: string) => {
              this.setState({ searchType: value as searchType });
              this.clearPage(false, true);
            }}
          />
          {this.state.searchType === searchType.cpr ? (
            <Fstyr.Input
              value={this.state.cvrOrCprInput}
              label="CPR nummer"
              onChange={(value: string) => this.setState({ cvrOrCprInput: value })}
              validation={true}
              validationType={ValidationType.cpr}
              errorBodyText={"Skal være 10 cifre langt"}
              errorExampleText={"f.eks. 0101901234"}
              onEnterPressed={() => this.handleSearch()}
              setValid={(valid: boolean) => this.setState({ cvrOrCprIsValid: valid })}
              buttonClicked={this.state.searchButtonPressed}
              onValidatingButtonPressed={() => this.setState({ searchButtonPressed: true })}
            />
          ) : (
            <Fstyr.Input
              value={this.state.cvrOrCprInput}
              label="CVR nummer"
              onChange={(value: string) => this.setState({ cvrOrCprInput: value })}
              validation={true}
              validationType={ValidationType.cvr}
              errorBodyText={"Skal være 8 cifre langt"}
              errorExampleText={"f.eks. 12345678"}
              onEnterPressed={() => this.handleSearch()}
              setValid={(valid: boolean) => this.setState({ cvrOrCprIsValid: valid })}
              buttonClicked={this.state.searchButtonPressed}
              onValidatingButtonPressed={() => this.setState({ searchButtonPressed: true })}
            />
          )}
          <div className="fstyr-left-button-group">
            <Fstyr.Button
              label="Søg"
              onClick={() => {
                this.handleSearch();
              }}
              disabled={this.state.showSpinner}
            />
            <div style={{ width: 20 }} />
            <Fstyr.Spinner show={this.state.showSpinner} />
          </div>
        </Fstyr.Segment>
        <Fstyr.Segment title="Virksomhedsinformation">
          <p className="fstyr-header-info">Virksomhedsoplysninger</p>
          <div className="fstyr-attention-box">
            <Fstyr.Input label={this.state.searchType} readonly={true} value={this.state.businessVm.Cvr === "0" ? "" : this.state.businessVm.Cvr} direction="row" />
            <Fstyr.Input label={this.state.searchType === searchType.cpr ? "Navn" : "Virksomhedsnavn"} readonly={true} value={this.state.businessVm.Name} direction="row" />
            <Fstyr.Input label={this.state.searchType + " Adresse"} readonly={true} value={formatAddress(this.state.businessVm.Address, true)} direction="row" />
          </div>
          <Fstyr.Table label="Registreret virksomhedskort" headers={["Kortnr.", "Navn", "Startdato", "Udløbsdato", "Status"]} data={this.state.tableResults} note={"Virksomhed har " + this.state.tableResults.length + " registreringer på virksomhedskort"} />
        </Fstyr.Segment>
      </>
    );
  }
}
